import { ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import {
	AddIcon,
	BellIcon,
	Button,
	ConversationIcon,
	DialogueIcon,
	GridIcon,
	Popover,
	TargetIcon,
	tokens,
} from '@treams/treams-ui';

const Grid = styled.div`
	display: grid;
	grid-template-columns: 70px 70px 70px 70px 70px;
	gap: ${tokens.gap.list}px;
	padding: ${tokens.padding.container}px;
`;

const Block = styled(Flex)`
	background: ${tokens.surface.secondary};
	border-radius: ${tokens.radius.md}px;
	height: 70px;
	cursor: pointer;

	&:hover {
		background: ${tokens.surface.hover};
	}

	&:active {
		background: ${tokens.surface.secondary};
	}
`;

const BlockTitle = styled.div`
	text-align: center;
	color: ${tokens.text.secondary};
	font-size: ${tokens.typography.sizes.small}px;
	margin-top: ${tokens.gap.inline}px;
`;

function BlockButton(props: {
	icon: ReactNode;
	title: string;
	type: graphql.MomentType;
	onSelect: (type: graphql.MomentType) => void;
}) {
	return (
		<Block
			flexDirection="column"
			alignItems="center"
			justifyContent="center"
			onClick={() => props.onSelect(props.type)}
		>
			<Box size={24} color={tokens.icon.primary}>
				{props.icon}
			</Box>
			<BlockTitle>{props.title}</BlockTitle>
		</Block>
	);
}

export default function AddMomentButton(props: { onSelect: (type: graphql.MomentType) => void }) {
	const { t } = useTranslation();
	const [active, setActive] = useState(false);

	const onSelect = (type: graphql.MomentType) => {
		setActive(false);
		props.onSelect(type);
	};

	return (
		<Popover.Target
			visible={active}
			onDismiss={() => setActive(false)}
			contents={
				<Grid>
					<BlockButton
						title={t('common.settings.cycleTemplates.momentTypes.evaluation')}
						icon={<ConversationIcon />}
						onSelect={onSelect}
						type={graphql.MomentType.Evaluation}
					/>
					<BlockButton
						title={t('common.settings.cycleTemplates.momentTypes.feedback')}
						icon={<DialogueIcon />}
						onSelect={onSelect}
						type={graphql.MomentType.Feedback}
					/>
					<BlockButton
						title={t('common.settings.cycleTemplates.momentTypes.goals')}
						icon={<TargetIcon />}
						onSelect={onSelect}
						type={graphql.MomentType.Goals}
					/>
					<BlockButton
						title={t('common.settings.cycleTemplates.momentTypes.message')}
						icon={<BellIcon />}
						onSelect={onSelect}
						type={graphql.MomentType.Message}
					/>
					<BlockButton
						title={t('common.settings.cycleTemplates.momentTypes.talentreview')}
						icon={<GridIcon />}
						onSelect={onSelect}
						type={graphql.MomentType.TalentReview}
					/>
				</Grid>
			}
		>
			<Button
				onClick={() => setActive(true)}
				title={t('common.settings.cycleTemplates.edit.addMoment')}
				icon={<AddIcon />}
				type="secondary"
			/>
		</Popover.Target>
	);
}
